import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    CheckCircleIcon,
    PencilSquareIcon,
    ArrowPathIcon,
    XMarkIcon,
    CheckBadgeIcon
} from '@heroicons/react/24/outline';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';

const ActiveMarkingPage = () => {
    const { assignmentId, submissionId } = useParams();
    const navigate = useNavigate();
    const { get, patch, post } = useApi();
    const { showToast } = useAppState();
    const { user } = useAuth();
    const canvasRef = React.useRef(null);

    const isStudent = user?.role === 'student';

    const [assignment, setAssignment] = useState(null);
    const [submission, setSubmission] = useState(null);
    const [submissions, setSubmissions] = useState([]); // List for paging
    const [loading, setLoading] = useState(true);
    const [navigating, setNavigating] = useState(false);
    const [saving, setSaving] = useState(false);


    // Marking State: Array of Blocks { id, start, end, points }
    const [marks, setMarks] = useState([]);
    const [saveStatus, setSaveStatus] = useState('saved'); // 'saved' is the steady state

    // Selection State
    const [selection, setSelection] = useState({ start: null, end: null, active: false, confirmed: false });
    const [hoverBlock, setHoverBlock] = useState(null);
    const [editingBlock, setEditingBlock] = useState(null);
    const [editingWrongBlock, setEditingWrongBlock] = useState(null);
    const [tempValue, setTempValue] = useState('');
    const [tempRemark, setTempRemark] = useState('');
    const [feedback, setFeedback] = useState('');

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [assignmentRes, submissionRes, allSubmissionsRes] = await Promise.all([
                get(`/api/assignments/${assignmentId}/`),
                get(`/api/assignment-submissions/${submissionId}/`),
                get(`/api/assignments/${assignmentId}/submissions/`)
            ]);

            let currentSubmission = submissionRes;

            // ON-DEMAND TRIGGER: If file exists but text is empty, extract now (PDF or DOCX)
            if (!currentSubmission.text_response && currentSubmission.file &&
                ['.pdf', '.docx', '.doc'].some(ext => currentSubmission.file.toLowerCase().endsWith(ext))) {
                try {
                    const extracted = await post(`/api/assignment-submissions/${submissionId}/extract_submission_text/`);
                    currentSubmission = extracted;
                } catch (err) {
                    console.error('Auto-extraction failed:', err);
                }
            }


            setAssignment(assignmentRes);
            setSubmission(currentSubmission);
            setSubmissions(allSubmissionsRes);
            setFeedback(currentSubmission.feedback || '');

            // Set initial marks from submission
            if (currentSubmission.marking_data) {
                const data = currentSubmission.marking_data;
                if (Array.isArray(data)) {
                    setMarks(data);
                } else {
                    // Backwards compatibility for row-based marks
                    const legacy = Object.entries(data).map(([idx, pts]) => ({
                        id: `legacy-${idx}`,
                        start: parseInt(idx),
                        end: parseInt(idx),
                        points: pts
                    }));
                    setMarks(legacy);
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            showToast('Failed to load marking session', 'error');
        } finally {
            setLoading(false);
        }
    }, [assignmentId, submissionId, get, post, showToast]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Guards
    const isOwner = submission?.student === user?.id;



    // Background Auto-Save Effect (Teachers Only)
    useEffect(() => {
        if (loading || !submission || isStudent) return;

        const syncProgress = async () => {
            try {
                setSaveStatus('saving');
                await patch(`/api/assignment-submissions/${submissionId}/`, {
                    marking_data: marks,
                    feedback: feedback
                });
                setSaveStatus('saved');
            } catch (error) {
                console.error('Auto-save error:', error);
                setSaveStatus('error');
            }
        };

        const timer = setTimeout(() => {
            // Only sync if marks or feedback have actually changed from initial load
            const hasMarksChanged = JSON.stringify(marks) !== JSON.stringify(submission.marking_data);
            const hasFeedbackChanged = feedback !== (submission.feedback || '');

            if (hasMarksChanged || hasFeedbackChanged) {
                syncProgress();
            }
        }, 1500); // 1.5s debounce

        return () => clearTimeout(timer);
    }, [marks, feedback, submissionId, patch, loading, submission, isStudent]);


    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (canvasRef.current && !canvasRef.current.contains(e.target)) {
                setEditingBlock(null);
                setEditingWrongBlock(null);
                setSelection({ start: null, end: null, active: false, confirmed: false });
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const rows = useMemo(() => {
        if (!submission?.text_response) return [];
        return submission.text_response.split('\n').filter(r => r.trim() !== '' || r === '');
    }, [submission?.text_response]);

    const totalPoints = useMemo(() => {
        return marks.reduce((sum, block) => sum + parseFloat(block.points || 0), 0);
    }, [marks]);

    const maxPoints = assignment?.total_marks || 20;
    const percentage = (totalPoints / maxPoints) * 100;

    const cbcLevel = useMemo(() => {
        if (percentage >= 70) return { code: 'EE', label: 'Exceeding Expectations', color: 'text-emerald-600', bg: 'bg-emerald-50' };
        if (percentage >= 50) return { code: 'ME', label: 'Meeting Expectations', color: 'text-indigo-600', bg: 'bg-indigo-50' };
        if (percentage >= 30) return { code: 'AE', label: 'Approaching Expectations', color: 'text-slate-600', bg: 'bg-slate-50' };
        return { code: 'BE', label: 'Below Expectations', color: 'text-rose-600', bg: 'bg-rose-50' };
    }, [percentage]);

    const navigation = useMemo(() => {
        if (!submissions.length || !submissionId) return { prev: null, next: null };
        const currentIndex = submissions.findIndex(s => s.id === parseInt(submissionId));
        return {
            prev: currentIndex > 0 ? submissions[currentIndex - 1].id : null,
            next: currentIndex < submissions.length - 1 ? submissions[currentIndex + 1].id : null
        };
    }, [submissions, submissionId]);

    const hasSubstantiveChanges = useMemo(() => {
        if (!submission) return false;
        const hasMarksChanged = JSON.stringify(marks) !== JSON.stringify(submission.marking_data);
        const hasFeedbackChanged = feedback !== (submission.feedback || '');
        return hasMarksChanged || hasFeedbackChanged;
    }, [marks, feedback, submission]);

    const isAlreadyGraded = submission?.status === 'graded';
    const showGradedLabel = isAlreadyGraded && !hasSubstantiveChanges;

    // Student Access Guards (placed after all hooks)
    if (isStudent && submission && !isOwner) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-8 text-center">
                <XMarkIcon className="w-16 h-16 text-rose-300 mb-6" />
                <h2 className="text-2xl font-black text-[#18216D] uppercase tracking-tight italic">Access Restricted</h2>
                <p className="text-slate-500 mt-2 font-medium">You are not authorized to view this submission.</p>
                <button onClick={() => navigate('/dashboard')} className="mt-8 px-8 py-4 bg-[#18216D] text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl">Return to Dashboard</button>
            </div>
        );
    }

    if (isStudent && submission && !isAlreadyGraded) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-8 text-center">
                <ArrowPathIcon className="w-16 h-16 text-indigo-300 mb-6 animate-spin" />
                <h2 className="text-2xl font-black text-[#18216D] uppercase tracking-tight italic">Work Under Review</h2>
                <p className="text-slate-500 mt-2 font-medium italic">Your teacher is currently assessing your high-velocity work. Check back soon!</p>
                <button onClick={() => navigate('/dashboard')} className="mt-8 px-8 py-4 bg-white border border-slate-200 text-[#18216D] rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-sm">Dashboard</button>
            </div>
        );
    }

    if (loading || navigating) return <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <ArrowPathIcon className="w-10 h-10 text-indigo-600 animate-spin" />
        <p className="mt-4 text-[10px] font-black uppercase tracking-widest text-slate-400">
            {navigating ? 'Syncing Progress...' : 'Initializing Marking Engine...'}
        </p>
    </div>;

    if (!assignment || !submission) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-8 text-center">
                <XMarkIcon className="w-16 h-16 text-rose-300 mb-6" />
                <h2 className="text-2xl font-black text-[#18216D] uppercase tracking-tight italic">Engine Error</h2>
                <p className="text-slate-500 mt-2 font-medium">Unable to load the marking context. Please refresh or contact support.</p>
                <button onClick={() => navigate(-1)} className="mt-8 px-8 py-4 bg-[#18216D] text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl">Return and Retry</button>
            </div>
        );
    }

    const handleNavigate = async (targetId) => {
        if (!targetId || navigating) return;

        try {
            setNavigating(true);
            setSaveStatus('saving');
            // Force save current marks before leaving
            await patch(`/api/assignment-submissions/${submissionId}/`, {
                marking_data: marks
            });
            setSaveStatus('saved');
            navigate(`/teacher/assignments/${assignmentId}/mark/${targetId}`);
        } catch (error) {
            console.error('Navigation save error:', error);
            showToast('Progress might not have saved correctly', 'error');
            // Navigate anyway but warn
            navigate(`/teacher/assignments/${assignmentId}/mark/${targetId}`);
        } finally {
            setNavigating(false);
        }
    };

    const handleMouseDown = (index) => {
        if (isStudent || rows[index].trim() === '') return;
        setSelection({ start: index, end: index, active: true, confirmed: false });
        setEditingBlock(null);
        setEditingWrongBlock(null);
    };

    const handleMouseEnterRow = (index) => {
        if (selection.active) {
            setSelection(prev => ({ ...prev, end: index }));
        }
    };

    const handleMouseUp = () => {
        if (!selection.active) return;

        const sel = window.getSelection();
        let charStart = undefined;
        let charEnd = undefined;
        
        if (sel.rangeCount > 0 && !sel.isCollapsed) {
            const range = sel.getRangeAt(0);
            const pElement = range.startContainer.nodeType === 3 ? range.startContainer.parentNode : range.startContainer;
            const pElementClosest = pElement.closest ? pElement.closest('p.row-text') : null;

            if (pElementClosest) {
                const preSelectionRange = range.cloneRange();
                preSelectionRange.selectNodeContents(pElementClosest);
                preSelectionRange.setEnd(range.startContainer, range.startOffset);
                charStart = preSelectionRange.toString().length;
                charEnd = charStart + sel.toString().length;
            }
        }

        setSelection(prev => ({ ...prev, active: false, confirmed: true, charStart, charEnd }));
    };

    const startMarking = (block) => {
        const newBlock = { ...block };
        if (newBlock.id === 'new') {
            newBlock.charStart = selection.charStart;
            newBlock.charEnd = selection.charEnd;
        }
        setEditingBlock(newBlock);
        setEditingWrongBlock(null);
        setTempValue(newBlock.points || '');
        setSelection({ start: null, end: null, active: false, confirmed: false });
    };

    const startMarkingWrong = (block) => {
        const newBlock = { ...block };
        if (newBlock.id === 'new') {
            newBlock.charStart = selection.charStart;
            newBlock.charEnd = selection.charEnd;
        }
        setEditingWrongBlock(newBlock);
        setEditingBlock(null);
        setTempRemark(newBlock.remark || '');
        setSelection({ start: null, end: null, active: false, confirmed: false });
    };

    const handleSaveBlock = () => {
        const val = parseFloat(tempValue);
        if (isNaN(val)) return setEditingBlock(null);

        if (editingBlock.id === 'new') {
            const newBlock = {
                id: Date.now().toString(),
                start: editingBlock.start,
                end: editingBlock.end,
                charStart: editingBlock.charStart,
                charEnd: editingBlock.charEnd,
                points: val
            };
            setMarks([...marks, newBlock]);
        } else {
            setMarks(marks.map(b => b.id === editingBlock.id ? { ...b, points: val } : b));
        }
        setEditingBlock(null);
        setTempValue('');
    };

    const handleSaveWrongBlock = () => {
        if (editingWrongBlock.id === 'new') {
            const newBlock = {
                id: Date.now().toString(),
                start: editingWrongBlock.start,
                end: editingWrongBlock.end,
                charStart: editingWrongBlock.charStart,
                charEnd: editingWrongBlock.charEnd,
                points: 0,
                is_wrong: true,
                remark: tempRemark
            };
            setMarks([...marks, newBlock]);
        } else {
            setMarks(marks.map(b => b.id === editingWrongBlock.id ? { ...b, remark: tempRemark } : b));
        }
        setEditingWrongBlock(null);
        setTempRemark('');
    };

    const handleDeleteBlock = (id) => {
        setMarks(marks.filter(b => b.id !== id));
    };

    const handleFinalize = async () => {
        try {
            setSaving(true);
            const payload = {
                grade: totalPoints,
                competency_level: cbcLevel.code,
                marking_data: marks,
                feedback: feedback,
                status: 'graded'
            };

            await patch(`/api/assignment-submissions/${submissionId}/`, payload);

            const outcomes = [assignment.learning_outcome, ...(assignment.tested_outcomes || [])].filter(Boolean);
            for (const outcomeId of outcomes) {
                await post('/api/cbc/competency-assessments/', {
                    student: submission.student,
                    learning_outcome: outcomeId,
                    competency_level: cbcLevel.code,
                    teacher: assignment.teacher,
                    evidence: `Active Marking: ${assignment.title}`,
                    assignment_submission: submission.id
                });
            }

            showToast('Assessment successfully finalized');
            // LOCAL UPDATE: Instead of navigating, update the local submission status
            setSubmission(prev => ({ ...prev, status: 'graded', competency_level: cbcLevel.code, grade: totalPoints }));
            setSaveStatus('saved');
            // navigate(`/teacher/assignments/${assignmentId}/submissions`); // REMOVED
        } catch (error) {
            console.error('Finalize error:', error);
            showToast('Failed to save assessment', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] flex flex-col" onMouseUp={handleMouseUp}>
            {/* Header */}
            <div className="bg-white border-b border-slate-200 px-8 py-4 sticky top-0 z-50 shadow-sm flex items-center justify-between">
                <div className="flex items-center gap-6">
                    <button onClick={() => navigate(`/teacher/assignments/${assignmentId}/submissions`)} className="p-2 hover:bg-slate-100 rounded-xl transition-all">
                        <ChevronLeftIcon className="w-6 h-6 text-slate-500" />
                    </button>
                    <div>
                        <h1 className="text-xl font-black text-[#18216D] tracking-tight">{submission?.student_name || 'Individual Scholar'}</h1>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{assignment?.title || 'Assessment Context'}</p>
                    </div>
                </div>

                <div className="flex items-center gap-8 bg-slate-50 px-8 py-3 rounded-2xl border border-slate-100">
                    <div className="text-center">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">Current Score</p>
                        <p className="text-lg font-black text-[#18216D]">{totalPoints} <span className="text-slate-300">/ {maxPoints}</span></p>
                    </div>
                    <div className="w-px h-8 bg-slate-200" />
                    <div className="text-center">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">Achievement</p>
                        <p className={`text-lg font-black ${cbcLevel.color}`}>{Math.round(percentage)}%</p>
                    </div>
                    <div className="px-5 py-2 rounded-xl border flex flex-col items-center justify-center min-w-[140px] transition-all duration-500 border-indigo-100 bg-indigo-50/50">
                        <p className="text-[8px] font-black uppercase tracking-widest text-indigo-400">Assignment Level</p>
                        <p className={`text-[11px] font-black ${cbcLevel.color}`}>{cbcLevel.code} - {cbcLevel.label}</p>
                    </div>
                </div>

                {!isStudent && (
                    <div className="flex flex-col items-end gap-2">
                        <button
                            onClick={handleFinalize}
                            disabled={saving || showGradedLabel}
                            className={`px-8 py-3.5 rounded-xl font-black text-[11px] uppercase tracking-widest transition-all shadow-lg flex items-center gap-2 ${showGradedLabel
                                ? 'bg-emerald-500 text-white cursor-default'
                                : 'bg-[#18216D] text-white hover:bg-[#0D164F]'
                                }`}
                        >
                            {saving ? (
                                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                            ) : showGradedLabel ? (
                                <CheckBadgeIcon className="w-5 h-5" />
                            ) : (
                                <CheckBadgeIcon className="w-5 h-5 text-indigo-200" />
                            )}
                            {showGradedLabel ? 'Graded' : isAlreadyGraded ? 'Update Assessment' : 'Finalize Assessment'}
                        </button>

                        <div className="flex items-center gap-1.5 pr-1">
                            <div className={`w-1 h-1 rounded-full transition-all duration-500 ${saveStatus === 'saving' ? 'bg-amber-400 animate-pulse' :
                                saveStatus === 'error' ? 'bg-rose-500' : 'bg-emerald-500'
                                }`} />
                            <span className="text-[8px] font-black uppercase tracking-widest text-slate-400">
                                {saveStatus === 'saving' ? 'Saving progress...' :
                                    saveStatus === 'error' ? 'Cloud error' : 'All progress saved'}
                            </span>
                        </div>
                    </div>
                )}

                {isStudent && (
                    <div className="px-6 py-2.5 bg-emerald-50 border border-emerald-100 rounded-xl flex items-center gap-3">
                        <CheckBadgeIcon className="w-5 h-5 text-emerald-600" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Feedback Secured</span>
                    </div>
                )}
            </div>

            {/* Edge Navigation Icons (Teachers Only) */}
            {!isStudent && navigation.prev && (
                <button
                    onClick={() => handleNavigate(navigation.prev)}
                    className="fixed left-4 top-1/2 -translate-y-1/2 p-3 bg-white/50 backdrop-blur-sm border border-slate-200 text-slate-400 hover:text-indigo-600 hover:bg-white hover:border-indigo-100 rounded-full shadow-sm transition-all z-[60] group active:scale-95"
                    title="Previous Student"
                >
                    <ChevronLeftIcon className="w-6 h-6 group-hover:-translate-x-0.5 transition-transform" />
                </button>
            )}

            {!isStudent && navigation.next && (
                <button
                    onClick={() => handleNavigate(navigation.next)}
                    className="fixed right-4 top-1/2 -translate-y-1/2 p-3 bg-white/50 backdrop-blur-sm border border-slate-200 text-slate-400 hover:text-indigo-600 hover:bg-white hover:border-indigo-100 rounded-full shadow-sm transition-all z-[60] group active:scale-95"
                    title="Next Student"
                >
                    <ChevronRightIcon className="w-6 h-6 group-hover:translate-x-0.5 transition-transform" />
                </button>
            )}

            {/* Canvas */}
            <div className="flex-1 overflow-y-auto p-12 flex justify-center bg-slate-100/30">
                <div ref={canvasRef} className="max-w-6xl w-full bg-white rounded-[3rem] shadow-2xl shadow-indigo-900/5 border border-white/20 flex relative min-h-[80vh]">
                    <div className="flex-1 relative bg-white pb-32 rounded-[3rem]">
                        {/* Lines */}
                        <div className="absolute inset-0 pointer-events-none rounded-[3rem] overflow-hidden"
                            style={{ backgroundSize: '100% 2.8rem', backgroundImage: 'linear-gradient(to bottom, transparent 2.75rem, #f1f5f9 2.8rem)' }} />

                        <div className="relative pt-12 px-20 pr-48"> {/* Increased padding right for gutter */}
                            {rows.map((row, index) => {
                                const isEmpty = row.trim() === '';

                                // Selection context
                                const isBeingSelected = (selection.active || selection.confirmed) &&
                                    index >= Math.min(selection.start, selection.end) &&
                                    index <= Math.max(selection.start, selection.end);

                                const isSelectionEnd = selection.confirmed && index === Math.max(selection.start, selection.end);

                                // Block context
                                const activeBlock = marks.find(b => index >= b.start && index <= b.end);
                                const isHovered = hoverBlock === activeBlock?.id;
                                const isEditing = editingBlock?.start <= index && editingBlock?.end >= index;
                                const isEditingWrong = editingWrongBlock?.start <= index && editingWrongBlock?.end >= index;
                                const isWrong = activeBlock && (activeBlock.is_wrong || activeBlock.points === 0);

                                const renderRowText = () => {
                                    if (activeBlock && activeBlock.charStart !== undefined && activeBlock.charEnd !== undefined && activeBlock.start === index) {
                                        const before = row.substring(0, activeBlock.charStart);
                                        const marked = row.substring(activeBlock.charStart, activeBlock.charEnd);
                                        const after = row.substring(activeBlock.charEnd);
                                        
                                        return (
                                            <>
                                                {before}
                                                <span className={`px-1 py-0.5 rounded-md ${isWrong ? 'bg-rose-200 text-rose-900' : 'bg-amber-200 text-amber-900'} transition-all`}>
                                                    {marked}
                                                </span>
                                                {after}
                                            </>
                                        );
                                    }

                                    if (isBeingSelected && selection.charStart !== undefined && selection.charEnd !== undefined && index === selection.start) {
                                        const before = row.substring(0, selection.charStart);
                                        const marked = row.substring(selection.charStart, selection.charEnd);
                                        const after = row.substring(selection.charEnd);

                                        return (
                                            <>
                                                {before}
                                                <span className="px-1 py-0.5 rounded-md bg-indigo-200 text-indigo-900 transition-all">
                                                    {marked}
                                                </span>
                                                {after}
                                            </>
                                        );
                                    }

                                    return row;
                                };

                                return (
                                    <div
                                        key={index}
                                        onMouseDown={() => handleMouseDown(index)}
                                        onMouseEnter={() => handleMouseEnterRow(index)}
                                        className={`group relative flex items-start min-h-[2.8rem] transition-all duration-200 cursor-text
                                            ${isEmpty ? 'pointer-events-none' : ''}
                                            ${isHovered ? (isWrong ? 'bg-rose-100/70' : 'bg-amber-100/70') : ''}
                                            ${isEditing ? 'bg-emerald-100/70' : ''}
                                            ${isEditingWrong ? 'bg-rose-100/70' : ''}
                                            ${activeBlock && !isHovered && !isEditing && !isEditingWrong ? (isWrong ? 'bg-rose-50/60' : 'bg-slate-50/60') : ''}
                                        `}
                                    >
                                        <p className={`row-text text-lg font-medium w-full whitespace-pre-wrap leading-[2.8rem] transition-all select-text
                                            ${activeBlock || isEditing || isEditingWrong || isBeingSelected ? (isWrong || isEditingWrong ? 'text-rose-900 font-bold' : 'text-indigo-900 font-bold') : 'text-slate-700'}
                                        `}>
                                            {renderRowText()}
                                        </p>

                                        {/* Gutter Logic - Moved INSIDE the row's expanded right area */}
                                        <div
                                            className="absolute top-0 -right-40 h-[2.8rem] flex items-center justify-end w-40 pr-8 z-40"
                                            onMouseDown={(e) => e.stopPropagation()}
                                        >
                                            {/* 1. Point Input (Editing) */}
                                            {isEditing && editingBlock.end === index && (
                                                <div className="bg-white border-2 border-[#18216D] rounded-[1rem] shadow-xl flex items-center p-1.5 animate-in zoom-in duration-200 z-50">
                                                    <input
                                                        autoFocus
                                                        type="number"
                                                        step="0.5"
                                                        value={tempValue}
                                                        onChange={(e) => setTempValue(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleSaveBlock();
                                                            if (e.key === 'Escape') setEditingBlock(null);
                                                        }}
                                                        className="w-16 bg-transparent text-[#18216D] text-center font-black text-sm outline-none placeholder:text-slate-300"
                                                        placeholder="pts"
                                                    />
                                                    <button
                                                        onClick={handleSaveBlock}
                                                        className="p-2.5 bg-[#18216D] text-white rounded-lg hover:bg-[#0D164F] active:scale-90 transition-all"
                                                    >
                                                        <CheckCircleIcon className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            )}

                                            {/* 1.5. Remark Input (Editing Wrong) */}
                                            {isEditingWrong && editingWrongBlock.end === index && (
                                                <div className="bg-white border-2 border-rose-500 rounded-[1rem] shadow-xl flex items-center p-1.5 animate-in zoom-in duration-200 z-50">
                                                    <input
                                                        autoFocus
                                                        type="text"
                                                        value={tempRemark}
                                                        onChange={(e) => setTempRemark(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleSaveWrongBlock();
                                                            if (e.key === 'Escape') setEditingWrongBlock(null);
                                                        }}
                                                        className="w-32 bg-transparent text-rose-700 text-left px-2 font-black text-sm outline-none placeholder:text-rose-300"
                                                        placeholder="Remark (opt)"
                                                    />
                                                    <button
                                                        onClick={handleSaveWrongBlock}
                                                        className="p-2.5 bg-rose-500 text-white rounded-lg hover:bg-rose-700 active:scale-90 transition-all"
                                                    >
                                                        <CheckCircleIcon className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            )}

                                            {/* 2. Selection Trigger - Show only if row is actually part of a multi-row selection */}
                                            {isSelectionEnd && !editingBlock && !editingWrongBlock && Math.abs(selection.start - selection.end) > 0 && (
                                                <div className="flex gap-2 bg-white p-2 rounded-2xl shadow-xl animate-in zoom-in duration-200 border border-slate-100 z-50 absolute right-0">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            startMarking({ id: 'new', start: Math.min(selection.start, selection.end), end: Math.max(selection.start, selection.end) });
                                                        }}
                                                        className="p-2 bg-emerald-50 text-emerald-600 rounded-xl shadow-sm hover:scale-105 active:scale-95 transition-all border border-emerald-100 flex items-center gap-2"
                                                        title="Add Mark"
                                                    >
                                                        <CheckCircleIcon className="w-5 h-5" />
                                                        <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">Mark</span>
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            startMarkingWrong({ id: 'new', start: Math.min(selection.start, selection.end), end: Math.max(selection.start, selection.end) });
                                                        }}
                                                        className="p-2 bg-rose-50 text-rose-600 rounded-xl shadow-sm hover:scale-105 active:scale-95 transition-all border border-rose-100 flex items-center gap-2"
                                                        title="Mark Wrong"
                                                    >
                                                        <XMarkIcon className="w-5 h-5" />
                                                        <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">Wrong</span>
                                                    </button>
                                                </div>
                                            )}

                                            {/* 3. Existing Mark - Persistent visibility */}
                                            {activeBlock && activeBlock.end === index && !isEditing && !isEditingWrong && (
                                                <div
                                                    className="flex items-center gap-1.5 group/mark"
                                                    onMouseEnter={() => setHoverBlock(activeBlock.id)}
                                                    onMouseLeave={() => setHoverBlock(null)}
                                                >
                                                    {!isStudent && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteBlock(activeBlock.id); }}
                                                            className="p-2 bg-rose-50 text-rose-500 rounded-lg border border-rose-100 hover:bg-rose-500 hover:text-white transition-all opacity-0 group-hover/mark:opacity-100 shadow-sm"
                                                        >
                                                            <XMarkIcon className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    {(activeBlock.is_wrong || activeBlock.points === 0) ? (
                                                        <div className="relative group/remark flex items-center">
                                                            <button
                                                                onClick={(e) => {
                                                                    if (isStudent) return;
                                                                    e.stopPropagation();
                                                                    startMarkingWrong(activeBlock);
                                                                }}
                                                                className={`px-4 py-2 rounded-xl border font-black text-[12px] shadow-sm transition-all flex items-center gap-2
                                                                    ${isHovered ? 'bg-rose-200 border-rose-400 text-rose-800 scale-105' : 'bg-rose-100/80 border-rose-200 text-rose-600'}
                                                                    ${isStudent ? 'cursor-default' : ''}
                                                                `}
                                                            >
                                                                <XMarkIcon className="w-4 h-4" />
                                                                {!isStudent && <PencilSquareIcon className="w-4 h-4 opacity-0 group-hover/remark:opacity-100" />}
                                                            </button>
                                                            {activeBlock.remark && (
                                                                <div className="absolute top-full mt-2 right-0 bg-white border border-rose-100 shadow-xl rounded-xl p-3 z-50 min-w-[200px] hidden group-hover/remark:block">
                                                                    <p className="text-xs font-semibold text-rose-900">{activeBlock.remark}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={(e) => {
                                                                if (isStudent) return;
                                                                e.stopPropagation();
                                                                startMarking(activeBlock);
                                                            }}
                                                            className={`px-4 py-2 rounded-xl border font-black text-[12px] shadow-sm transition-all flex items-center gap-2
                                                                ${isHovered ? 'bg-amber-200 border-amber-400 text-amber-800 scale-105' : 'bg-amber-100/80 border-amber-200 text-amber-600'}
                                                                ${isStudent ? 'cursor-default' : ''}
                                                            `}
                                                        >
                                                            +{activeBlock.points}
                                                            {!isStudent && <PencilSquareIcon className="w-4 h-4 opacity-0 group-hover:opacity-100" />}
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                            {/* 4. Default Hover (Single Line) - Teacher Only */}
                                            {!isStudent && !activeBlock && !editingBlock && !editingWrongBlock && !selection.active && !selection.confirmed && (
                                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-all">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            startMarking({ id: 'new', start: index, end: index });
                                                        }}
                                                        className="p-2.5 bg-emerald-50 text-emerald-500 hover:bg-emerald-600 hover:text-white rounded-xl border border-emerald-100 transition-all shadow-md active:scale-90"
                                                    >
                                                        <CheckCircleIcon className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            startMarkingWrong({ id: 'new', start: index, end: index });
                                                        }}
                                                        className="p-2.5 bg-rose-50 text-rose-500 hover:bg-rose-600 hover:text-white rounded-xl border border-rose-100 transition-all shadow-md active:scale-90"
                                                    >
                                                        <XMarkIcon className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {/* Reflective Feedback Section */}
                            <div className="mt-16 border-t-2 border-slate-50 pt-12 animate-in fade-in slide-in-from-bottom-4 duration-1000">
                                <div className="group relative flex items-start min-h-[2.8rem]">
                                    <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest leading-[2.8rem] flex items-center gap-2">
                                        <PencilSquareIcon className="w-4 h-4" />
                                        Reflective Feedback (optional):
                                    </span>
                                </div>

                                <div className="relative mt-2">
                                    <textarea
                                        readOnly={isStudent}
                                        value={feedback}
                                        onChange={(e) => setFeedback(e.target.value)}
                                        placeholder={isStudent ? "No reflective feedback provided." : "Type your reflective feedback here..."}
                                        className={`w-full bg-transparent border-none outline-none text-lg font-medium text-indigo-900 leading-[2.8rem] resize-none focus:ring-0 transition-all ${isStudent ? 'cursor-default' : 'placeholder:text-slate-300 placeholder:italic'}`}
                                        style={{
                                            minHeight: '11.2rem', // Exactly 4 rows
                                            backgroundSize: '100% 2.8rem',
                                            backgroundImage: 'linear-gradient(to bottom, transparent 2.75rem, #f1f5f9 2.8rem)'
                                        }}
                                    />
                                    {feedback.length > 0 && (
                                        <div className="absolute top-0 right-0 py-2.5">
                                            <span className="px-3 py-1 bg-indigo-50 text-indigo-500 rounded-lg text-[8px] font-black uppercase tracking-tight border border-indigo-100/50">
                                                Marking Feedback
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{ height: '10rem' }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Instruction Footer - Teacher Only */}
            {!isStudent && (
                <div className="bg-[#18216D] text-white/60 px-8 py-3 flex items-center justify-between text-[10px] uppercase font-black tracking-widest">
                    <div className="flex items-center gap-8">
                        <span className="flex items-center gap-2"><kbd className="bg-white/10 px-1.5 py-0.5 rounded border border-white/20 text-white">DRAG</kbd> Highlight Selection</span>
                        <span className="flex items-center gap-2"><kbd className="bg-white/10 px-1.5 py-0.5 rounded border border-white/20 text-white">CLICK MARK</kbd> Visual Recall</span>
                        <span className="flex items-center gap-2"><kbd className="bg-white/20 px-1.5 py-0.5 rounded text-white">ENTER</kbd> Save Points</span>
                    </div>
                    <div>Scholar Portal v3.0 • Intelligent Assessment Engine</div>
                </div>
            )}
        </div>
    );
};

export default ActiveMarkingPage;
